import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, X, Eye, Smile, Gift, Save } from 'lucide-react';
import { useCalculatorStore } from '../store/calculatorStore';
import toast from 'react-hot-toast';

interface CustomizerOption {
  label: string;
  value: string;
  preview?: React.ReactNode;
}

const eyeStyles: CustomizerOption[] = [
  { label: 'Happy', value: 'happy' },
  { label: 'Focused', value: 'focused' },
  { label: 'Sleepy', value: 'sleepy' }
];

const mouthStyles: CustomizerOption[] = [
  { label: 'Smile', value: 'smile' },
  { label: 'Determined', value: 'determined' },
  { label: 'Surprised', value: 'surprised' }
];

const accessories: CustomizerOption[] = [
  { label: 'None', value: 'none' },
  { label: 'Bow Tie', value: 'bowtie' },
  { label: 'Glasses', value: 'glasses' },
  { label: 'Hat', value: 'hat' }
];

export const CalculatorCustomizer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    eyeStyle,
    mouthStyle,
    accessory,
    setEyeStyle,
    setMouthStyle,
    setAccessory
  } = useCalculatorStore();

  const saveCustomization = () => {
    toast.success('Customization saved!');
    setIsOpen(false);
  };

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className="fixed top-4 right-4 p-3 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors z-50"
      >
        <Palette className="w-5 h-5" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ opacity: 0, x: 300 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 300 }}
              className="fixed right-0 top-0 h-full w-80 bg-[var(--background)] border-l border-emerald-500/20 p-6 z-50"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Customize Calculator</h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-slate-800/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="flex items-center gap-2 text-emerald-500">
                    <Eye className="w-4 h-4" />
                    Eye Style
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {eyeStyles.map((style) => (
                      <button
                        key={style.value}
                        onClick={() => setEyeStyle(style.value as any)}
                        className={`p-3 rounded-lg border transition-colors ${
                          eyeStyle === style.value
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-slate-700 hover:border-emerald-500/50'
                        }`}
                      >
                        {style.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="flex items-center gap-2 text-emerald-500">
                    <Smile className="w-4 h-4" />
                    Mouth Style
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {mouthStyles.map((style) => (
                      <button
                        key={style.value}
                        onClick={() => setMouthStyle(style.value as any)}
                        className={`p-3 rounded-lg border transition-colors ${
                          mouthStyle === style.value
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-slate-700 hover:border-emerald-500/50'
                        }`}
                      >
                        {style.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="flex items-center gap-2 text-emerald-500">
                    <Gift className="w-4 h-4" />
                    Accessories
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {accessories.map((item) => (
                      <button
                        key={item.value}
                        onClick={() => setAccessory(item.value as any)}
                        className={`p-3 rounded-lg border transition-colors ${
                          accessory === item.value
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-slate-700 hover:border-emerald-500/50'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={saveCustomization}
                  className="w-full px-4 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Customization
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};